import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Search, X, CheckCircle, XCircle, Clock, Eye, Loader2 } from 'lucide-react';
import { getPreviousDecisions, getLeaveRequest } from '../../../../api/Attendance/leaves';
import { PreviousDecisionView, LeaveTypeView, LeaveRequestView, LeaveRequestFilterParams } from '../../../../models/attendance';
import { getLeaveTypeBadgeStyle } from '../../../../utils/leaveTypeUtils';
import { getFullUrl } from '../../../../utils/url';
import { appDate } from '../../../../utils/appDateFormat';
import { DateField } from '../../../../components/dateFields';
import Pagination from '@/components/Pagination';
import RequestDetailModal from './RequestDetailModal';
import { parseApiDateOnly, parseApiUtc } from '@/utils/apiDate';

interface PreviousRequestsProps {
  leaveTypes?: LeaveTypeView[];
}

const PreviousRequests: React.FC<PreviousRequestsProps> = ({ leaveTypes }) => {
  const { t } = useTranslation(['leaves', 'common', 'translation']);
  const [requests, setRequests] = useState<PreviousDecisionView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<LeaveRequestFilterParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequestView | null>(null);
  const [loadingRequestId, setLoadingRequestId] = useState<number | null>(null);

  const loadRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getPreviousDecisions(filters);
      if (result.errors.length === 0) {
        setRequests(result.payload.items);
        setTotalCount(result.payload.totalCount);
        setTotalPages(result.payload.totalPages);
      }
    } catch { /* ignore */ }
    setIsLoading(false);
  }, [filters]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleFilterChange = (key: keyof LeaveRequestFilterParams, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined, pageNumber: 1 }));
  };

  const clearFilters = () => {
    setFilters({ pageNumber: 1, pageSize: 10 });
    setEmployeeSearch('');
  };

  const handleViewDetails = async (leaveRequestId: number) => {
    setLoadingRequestId(leaveRequestId);
    try {
      const result = await getLeaveRequest(leaveRequestId);
      if (result.errors.length === 0) {
        setSelectedRequest(result.payload);
      }
    } catch { /* ignore */ }
    setLoadingRequestId(null);
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'approved') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle size={12} className="ltr:mr-1 rtl:ml-1" />
          {t('Approved')}
        </span>
      );
    }
    if (statusLower === 'rejected') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle size={12} className="ltr:mr-1 rtl:ml-1" />
          {t('Rejected')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock size={12} className="ltr:mr-1 rtl:ml-1" />
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string): string => {
    return appDate(parseApiDateOnly(dateString));
  };

  return (
    <div className="bg-card border border-border rounded-xl" data-testid="previous-decisions">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground flex items-center">
          <History size={20} className="ltr:mr-2 rtl:ml-2" />
          {t('translation:previousRequests', 'Previous Requests')}
          <span className="ltr:ml-2 rtl:mr-2 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
            {totalCount}
          </span>
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t('translation:previousRequestsDesc', 'Historical record of processed leave requests')}
        </p>
      </div>

      {/* Filters */}
      <div className="p-6 border-b border-border bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">{t('Leave Type')}</label>
            <select
              value={filters.leaveTypeId ?? ''}
              onChange={(e) => handleFilterChange('leaveTypeId', e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary text-sm"
              data-testid="leave-previous-type-filter"
            >
              <option value="">{t('translation:allTypes', 'All Types')}</option>
              {leaveTypes?.filter(lt => lt.isActive).map(lt => (
                <option key={lt.id} value={lt.id}>{lt.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">{t('common:From Date')}</label>
            <DateField
              value={filters.fromDate || ''}
              onChange={(v) => handleFilterChange('fromDate', v || undefined)}
              data-testid="leave-previous-from-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">{t('common:To Date')}</label>
            <DateField
              value={filters.toDate || ''}
              onChange={(v) => handleFilterChange('toDate', v || undefined)}
              data-testid="leave-previous-to-date"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors flex items-center"
              data-testid="leave-previous-clear-filters"
            >
              <X size={14} className="ltr:mr-1 rtl:ml-1" />
              {t('translation:clearFilters', 'Clear Filters')}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('translation:employee', 'Employee')}
              </th>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('Leave Type')}
              </th>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('translation:dates', 'Dates')}
              </th>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('translation:status', 'Status')}
              </th>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('translation:decisionDate', 'Decision Date')}
              </th>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('Reviewer Comments')}
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center">
                  <div className="animate-pulse text-muted-foreground">{t('translation:loading', 'Loading...')}</div>
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <Search size={48} className="text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">{t('translation:noResultsFound', 'No requests found')}</h3>
                    <p className="text-muted-foreground">
                      {t('translation:adjustFilters', 'Try adjusting your filters to see more results.')}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              requests.map((req) => (
                <tr key={req.leaveRequestId} className="hover:bg-muted/30 cursor-pointer" onClick={() => handleViewDetails(req.leaveRequestId)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                        {getFullUrl(req.imageUrl) ? (
                          <img src={getFullUrl(req.imageUrl)!} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs font-medium text-muted-foreground">
                            {req.userName?.charAt(0) || '?'}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-foreground">{req.userName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLeaveTypeBadgeStyle(req.leaveType, leaveTypes).colorClass}`}
                      style={getLeaveTypeBadgeStyle(req.leaveType, leaveTypes).style}
                    >
                      {req.leaveType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {formatDate(req.startDate)} - {formatDate(req.endDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(req.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {appDate(parseApiUtc(req.decisionDate))}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground max-w-xs truncate">
                    {req.managerComments || '-'}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleViewDetails(req.leaveRequestId); }}
                      className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
                      title={t('View Details')}
                    >
                      {loadingRequestId === req.leaveRequestId ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3 border-t border-border bg-muted/30">
            <Pagination
                currentPage={filters.pageNumber || 1}
                totalPages={totalPages}
                onPageChange={(p) => setFilters(prev => ({ ...prev, pageNumber: p }))}
                variant="compact"
            />
        </div>
      )}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          leaveTypes={leaveTypes}
          onClose={() => setSelectedRequest(null)}
        />
      )}
    </div>
  );
};

export default PreviousRequests;
